import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MdDelete, MdSave } from 'react-icons/md';
import { RxDragHandleDots2 } from 'react-icons/rx';
import { notify } from 'utils';
import { CiEdit } from 'react-icons/ci';
import { ModalConfirm } from '../Modals/ModalConfirm';

function SortableItem({ poll, index, handleUpdate, handleDelete, isNewPoll, isIncident }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: poll.id });
  const [isEdit, setIsEdit] = useState(false);
  const [pollEdit, setPollEdit] = useState({});
  const [modalConfirm, setModalConfirm] = useState(false);
  const [modalConfirmUpdate, setModalConfirmUpdate] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const handleSaveClick = () => {
    if (!pollEdit.name.trim()) {
      notify("warning", "Campo nombre es requerido", 3000);
      return;
    }
    setModalConfirmUpdate(true); // Mostrar modal de confirmación para update
  };

  const handleUpdateConfirm = () => {
    const data = {
      name: pollEdit.name,
      required: pollEdit.required,
      order: pollEdit.order,
      isCheking : pollEdit.isCheking,
    };
    handleUpdate(data, pollEdit.id);
    setModalConfirmUpdate(false);
    setIsEdit(false);
  };


  return (
    <div
      key={index}
      ref={setNodeRef}
      style={style}
      className='h-14 w-full flex flex-row items-center bg-[#707070] rounded-lg px-5 mb-2'
    >
      {
        !isNewPoll ? (
          <div className='w-1/12 flex justify-center text-white cursor-move' {...listeners} {...attributes}>
            <RxDragHandleDots2 className="text-xl" />
          </div>
        ) : (
          <div className='w-1/12 flex justify-center text-white cursor-move' />
        )
      }
      <div className="w-1/6 lg:w-1/12">
        <div className="h-10 w-10 bg-[#e5e5e5] text-[#757575] rounded-lg flex items-center justify-center font-bold">
          {index + 1}
        </div>
      </div>
      <div className="w-2/6 lg:w-7/12">
        {
          isEdit ? (
            <input
              type='text'
              className="h-10 w-full lg:w-3/5 flex items-center justify-start truncate font-bold bg-[#e5e5e5] text-[#757575] rounded-lg px-3"
              value={pollEdit.name}
              maxLength={50}
              onChange={({ target }) => {
                setPollEdit({
                  ...pollEdit,
                  name: target.value
                })
              }}
            />
          ) : (
            <div className="h-10 w-full lg:w-3/5 flex items-center justify-start truncate font-bold bg-[#e5e5e5] text-[#757575] rounded-lg px-3">
              {poll.name}
            </div>
          )
        }
      </div>
      <div className='w-1/6 lg:w-3/12 flex flex-row space-x-4 text-white '>
        <div className='flex flex-col'>
          <label htmlFor='required'>Obligatorio</label>
          <input
            name={`option-radio-${index}`}
            type="radio"
            id={`required-${index}`}
            disabled={!isEdit}
            onClick={() => {
              setPollEdit({ ...pollEdit, required: !pollEdit.required });
            }}
            checked={isEdit ? pollEdit.required === true : poll.required === true}
          />
        </div>
        <div className='flex flex-col'>
          <label htmlFor='required'>Trackeo GPS</label>
          <input
            name={`option-isCheking-${index}`}
            type="radio"
            id={`isCheking-${index}`}
            onClick={() => {
              setPollEdit({ ...pollEdit, isCheking: !pollEdit.isCheking });
            }}
            checked={isEdit ? pollEdit.isCheking === true : poll.isCheking === true}
            disabled={isIncident || !isEdit}
          />
        </div>
      </div>
      <div className="w-1/6 lg:w-1/12 flex justify-end space-x-3">
        {
          isEdit ? (
            <div
              onClick={() => handleSaveClick()}
              className='h-10 w-10 flex justify-center items-center rounded-full bg-[#000000] text-white cursor-pointer hover:bg-[#ababab] hover:text-white font-bold'>
              <MdSave />
            </div>
          ) : (
            <div
              onClick={() => {
                setIsEdit(true)
                setPollEdit(poll)
              }}
              className='h-10 w-10 flex justify-center items-center rounded-full bg-[#000000] text-white cursor-pointer hover:bg-[#ababab] hover:text-white font-bold'>
              <CiEdit />
            </div>
          )
        }
        <div
          onClick={() => setModalConfirm(true)}
          className="h-10 w-10 flex justify-center items-center rounded-full bg-[#ababab] text-white cursor-pointer hover:text-black"
        >
          <MdDelete />
        </div>
      </div>
      {
        modalConfirm && (
          <ModalConfirm
            title="Eliminar sondeos"
            description="¿Estás seguro de eliminar este sondeo?"
            onAcept={() => handleDelete(poll.id)}
            onClose={() => setModalConfirm(false)}
            acceptDescription='Aceptar'
            discardDescription='Cancelar'
          />
        )
      }
      {
        modalConfirmUpdate && (
          <ModalConfirm
            title="Actualizar sondeo"
            description="¿Estás seguro de guardar los cambios?"
            onAcept={handleUpdateConfirm}
            onClose={() => setModalConfirmUpdate(false)}
            acceptDescription='Aceptar'
            discardDescription='Cancelar'
          />
        )
      }
    </div>
  );
}

function ProbeList({ localProbes, handleUpdate, handleDelete, handleChangeOrder, isNewPoll, isIncident }) {
  const sensors = useSensors(useSensor(PointerSensor));
  const [newPolls, setNewPolls] = useState([]);
  const [modalConfirm, setModalConfirm] = useState(false);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = localProbes.findIndex((item) => item.id === active.id);
    const newIndex = localProbes.findIndex((item) => item.id === over.id);

    const tmpNewPolls = arrayMove(localProbes, oldIndex, newIndex).map((item, idx) => ({
      ...item,
      order: idx + 1
    }));

    setNewPolls(tmpNewPolls);
    setModalConfirm(true);
  };

  return (
    <>
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={localProbes.map((item) => item.id)} strategy={verticalListSortingStrategy}>
        {localProbes.map((poll, index) => (
          <SortableItem
            key={poll.id}
            poll={poll}
            index={index}
            handleUpdate={handleUpdate}
            handleDelete={handleDelete}
            isNewPoll={isNewPoll}
            isIncident={isIncident}
          />
        ))}
      </SortableContext>
    </DndContext>
    {
      modalConfirm && (
        <ModalConfirm 
          title="Cambiar orden"
          description="¿Estás seguro de cambiar el orden de los sondeos?"
          onAcept={()=>{
            handleChangeOrder(newPolls)
            setModalConfirm(false)
          }}
          onClose={()=>setModalConfirm(false)}
          acceptDescription='Aceptar'
          discardDescription='Cancelar'
        />
      )
    }
    </>
  );
}

export default ProbeList;
